import * as THREE from 'three';
import { Track, Rng } from '@voidline/shared';
import { EnvironmentHandle } from './types.js';
import type { GraphicsPreset } from '../../persist.js';
import { glowSprite } from '../proctex.js';
import { sampleRibbon, clearOfRibbon } from './placement.js';

/**
 * AURORA REACH — frozen polar night. Undulating aurora curtains in green and
 * violet, snowfall around the camera, ice shards on a black glass plain and
 * jagged mountain silhouettes ringing the horizon.
 */
export function buildAuroraReach(scene: THREE.Scene, track: Track, quality: GraphicsPreset): EnvironmentHandle {
  const disposables: { dispose(): void }[] = [];
  const objects: THREE.Object3D[] = [];
  const rng = new Rng('aurora-reach');

  const ribbon = sampleRibbon(track, 80);
  const { cx, cy, cz } = ribbon;
  const groundY = ribbon.minY - 32;

  // ── sky: moonless polar night ──
  {
    const geo = new THREE.SphereGeometry(3800, 24, 16);
    const mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {},
      vertexShader: `
        varying vec3 vDir;
        void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
      `,
      fragmentShader: `
        varying vec3 vDir;
        void main(){
          float h = vDir.y * 0.5 + 0.5;
          vec3 zenith = vec3(0.004, 0.008, 0.018);
          vec3 horizon = vec3(0.020, 0.045, 0.065);
          vec3 col = mix(horizon, zenith, pow(h, 0.7));
          gl_FragColor = vec4(col, 1.0);
        }
      `,
    });
    const sky = new THREE.Mesh(geo, mat);
    scene.add(sky);
    objects.push(sky);
    disposables.push(geo, mat);
  }

  // ── starfield: dense and cold ──
  {
    const count = quality === 'performance' ? 1000 : 2400;
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      const a = rng.next() * Math.PI * 2;
      const z = rng.range(-0.05, 1);
      const r = Math.sqrt(Math.max(0, 1 - z * z));
      pos[i * 3] = Math.cos(a) * r * 3500;
      pos[i * 3 + 1] = z * 3500;
      pos[i * 3 + 2] = Math.sin(a) * r * 3500;
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const mat = new THREE.PointsMaterial({
      color: 0xd8f2ff,
      size: 2.0,
      sizeAttenuation: false,
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
    });
    const stars = new THREE.Points(geo, mat);
    scene.add(stars);
    objects.push(stars);
    disposables.push(geo, mat);
  }

  // ── lights: ice-blue hemisphere + faint aurora-tinted key ──
  {
    const hemi = new THREE.HemisphereLight(0x9fd8ff, 0x060a10, 0.7);
    const moon = new THREE.DirectionalLight(0xbfe6ff, 0.5);
    moon.position.set(cx - 500, cy + 600, cz + 700);
    const auroraFill = new THREE.DirectionalLight(0x52ffb0, 0.22);
    auroraFill.position.set(cx + 300, cy + 900, cz - 200);
    scene.add(hemi, moon, auroraFill);
    objects.push(hemi, moon, auroraFill);
  }

  // ── aurora curtains: undulating shader ribbons hung around the horizon ──
  const curtainMats: THREE.ShaderMaterial[] = [];
  {
    const curtainCount = quality === 'performance' ? 3 : 5;
    for (let i = 0; i < curtainCount; i++) {
      const width = rng.range(1700, 2800);
      const height = rng.range(520, 900);
      const geo = new THREE.PlaneGeometry(width, height, 56, 1);
      const mat = new THREE.ShaderMaterial({
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
        uniforms: {
          uTime: { value: 0 },
          uPhase: { value: rng.range(0, 100) },
        },
        vertexShader: `
          varying vec2 vUv;
          uniform float uTime; uniform float uPhase;
          void main(){
            vUv = uv;
            vec3 p = position;
            // slow undulation, stronger toward the top edge
            float w = uTime * 0.18 + uPhase;
            p.z += (sin(uv.x * 9.0 + w) * 60.0 + sin(uv.x * 23.0 - w * 1.7) * 24.0) * (0.25 + uv.y);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
          }
        `,
        fragmentShader: `
          varying vec2 vUv;
          uniform float uTime; uniform float uPhase;
          void main(){
            float t = uTime * 0.1 + uPhase;
            // vertical rays: layered drifting bands across x
            float rays =
              0.55 + 0.45 * sin(vUv.x * 40.0 + sin(vUv.x * 7.0 + t) * 3.0 + t * 1.3);
            rays *= 0.6 + 0.4 * sin(vUv.x * 13.0 - t * 0.7);
            // bright skirt at the bottom, long fade upward
            float skirt = smoothstep(0.0, 0.06, vUv.y) * pow(1.0 - vUv.y, 1.6);
            skirt += smoothstep(0.0, 0.02, vUv.y) * smoothstep(0.15, 0.0, vUv.y) * 0.8;
            vec3 green = vec3(0.15, 1.0, 0.45);
            vec3 violet = vec3(0.55, 0.20, 1.0);
            vec3 col = mix(green, violet, clamp(vUv.y * 1.4 - 0.1, 0.0, 1.0));
            gl_FragColor = vec4(col, rays * skirt * 0.55);
          }
        `,
      });
      curtainMats.push(mat);
      const curtain = new THREE.Mesh(geo, mat);
      const ang = (i / curtainCount) * Math.PI * 2 + rng.range(-0.4, 0.4);
      const dist = rng.range(1800, 2500);
      // hang curtains well above the highest track point: their planes are huge
      // and un-culled, so a low one can slice straight through the road
      curtain.position.set(cx + Math.cos(ang) * dist, cy + rng.range(750, 1000), cz + Math.sin(ang) * dist);
      curtain.rotation.y = -ang + Math.PI / 2 + rng.range(-0.3, 0.3);
      curtain.frustumCulled = false; // vertex shader sways it beyond its bounds
      scene.add(curtain);
      objects.push(curtain);
      disposables.push(geo, mat);
    }
  }

  // ── mountain silhouettes: two jagged rings, black against the sky ──
  const ridgeR = Math.max(1550, ribbon.maxR + 600);
  for (const [radius, peakLo, peakHi, shade] of [
    [ridgeR, 90, 260, 0x04070c],
    [ridgeR + 750, 180, 470, 0x030509],
  ] as const) {
    const SEG = 130;
    const pos = new Float32Array(SEG * 2 * 3);
    const idx: number[] = [];
    for (let i = 0; i < SEG; i++) {
      const a = (i / SEG) * Math.PI * 2;
      const r = radius + rng.range(-90, 90);
      const x = cx + Math.cos(a) * r;
      const z = cz + Math.sin(a) * r;
      // jagged profile: occasional sharp peaks between low saddles
      const peak = rng.chance(0.3) ? rng.range(peakHi * 0.6, peakHi) : rng.range(peakLo, peakHi * 0.45);
      pos[i * 6] = x;
      pos[i * 6 + 1] = groundY - 20;
      pos[i * 6 + 2] = z;
      pos[i * 6 + 3] = x;
      pos[i * 6 + 4] = groundY + peak;
      pos[i * 6 + 5] = z;
      const j = (i + 1) % SEG;
      idx.push(i * 2, j * 2, i * 2 + 1, i * 2 + 1, j * 2, j * 2 + 1);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setIndex(idx);
    const mat = new THREE.MeshBasicMaterial({ color: shade, side: THREE.DoubleSide, fog: false });
    const ridge = new THREE.Mesh(geo, mat);
    scene.add(ridge);
    objects.push(ridge);
    disposables.push(geo, mat);
  }

  // ── ground: black ice with a wet sheen ──
  {
    const geo = new THREE.PlaneGeometry(7000, 7000);
    const mat = new THREE.MeshStandardMaterial({ color: 0x0a1218, roughness: 0.22, metalness: 0.55 });
    const floor = new THREE.Mesh(geo, mat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(cx, groundY, cz);
    scene.add(floor);
    objects.push(floor);
    disposables.push(geo, mat);
  }

  // ── ice shards: tilted crystal spikes catching the aurora light ──
  {
    const count = quality === 'performance' ? 34 : 80;
    const geo = new THREE.ConeGeometry(1, 1, 5);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x16242e,
      emissive: 0x5effe0,
      emissiveIntensity: 0.1,
      roughness: 0.15,
      metalness: 0.7,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, count);
    const dummy = new THREE.Object3D();
    let placed = 0;
    let guard = 0;
    while (placed < count && guard++ < count * 30) {
      const x = cx + rng.range(-1300, 1300);
      const z = cz + rng.range(-1300, 1300);
      if (!clearOfRibbon(ribbon, x, z, 85)) continue;
      const h = rng.range(26, 130);
      dummy.position.set(x, groundY + h * 0.42, z);
      dummy.scale.set(rng.range(6, 22), h, rng.range(6, 22));
      dummy.rotation.set(rng.range(-0.25, 0.25), rng.range(0, Math.PI), rng.range(-0.25, 0.25));
      dummy.updateMatrix();
      mesh.setMatrixAt(placed, dummy.matrix);
      placed++;
    }
    mesh.count = placed;
    scene.add(mesh);
    objects.push(mesh);
    disposables.push(geo, mat);
  }

  // ── snowfall around the camera ──
  const snowCount = quality === 'performance' ? 260 : 640;
  const snow = (() => {
    const tex = glowSprite();
    disposables.push(tex);
    const geo = new THREE.PlaneGeometry(0.16, 0.16);
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
      color: 0xe8f6ff,
      side: THREE.DoubleSide,
    });
    const mesh = new THREE.InstancedMesh(geo, mat, snowCount);
    mesh.frustumCulled = false;
    disposables.push(geo, mat);
    const offsets: THREE.Vector3[] = [];
    const speeds: number[] = [];
    for (let i = 0; i < snowCount; i++) {
      offsets.push(new THREE.Vector3(rng.range(-38, 38), rng.range(-20, 24), rng.range(-38, 38)));
      speeds.push(rng.range(2.5, 6));
    }
    scene.add(mesh);
    objects.push(mesh);
    return { mesh, offsets, speeds };
  })();
  const snowDummy = new THREE.Object3D();

  let t = 0;
  return {
    update(dt, camPos, _time) {
      t += dt;
      // aurora undulation
      for (const m of curtainMats) m.uniforms.uTime.value = t;
      // snow drifts down with a lateral wander
      for (let i = 0; i < snow.offsets.length; i++) {
        const o = snow.offsets[i];
        o.y -= dt * snow.speeds[i];
        if (o.y < -20) o.y += 44;
        const swayX = Math.sin(t * 0.7 + i * 0.61) * 1.4;
        const swayZ = Math.cos(t * 0.5 + i * 1.13) * 1.4;
        snowDummy.position.set(camPos.x + o.x + swayX, camPos.y + o.y, camPos.z + o.z + swayZ);
        snowDummy.rotation.set(0, ((i * 41) % 100) * 0.0628, 0);
        snowDummy.updateMatrix();
        snow.mesh.setMatrixAt(i, snowDummy.matrix);
      }
      snow.mesh.instanceMatrix.needsUpdate = true;
    },
    dispose() {
      for (const o of objects) o.removeFromParent();
      for (const d of disposables) d.dispose();
    },
  };
}
